import { calculateCursorPosition } from "../../../../utilities/form/calculateCursorPosition";

export type ExpiryYearDigit = "two" | "four";

const MONTH_LENGTH = 2;

const yearLengthOf = (yearDigit: ExpiryYearDigit): number =>
  yearDigit === "four" ? 4 : 2;

const clampMonth = (month: string): string => {
  if (month.length !== MONTH_LENGTH) return month;
  const value = parseInt(month, 10);
  if (value > 12) return "12";
  if (value === 0) return "01";
  return month;
};

const digitsOf = (value: string): string => value.replace(/\D/g, "");

/**
 * Formats a card expiry as MM/YY (or MM/YYYY).
 *
 * The slash the user already has is a hard boundary between month and year.
 * Digits never move across it, so editing the month cannot steal a year
 * digit (RBC-2168). Without a slash the digits are split 2 + year, which
 * gives the auto-inserted slash while typing a fresh value.
 */
export const formatExpiryDate = (
  value: string,
  yearDigit: ExpiryYearDigit
): string => {
  const yearLength = yearLengthOf(yearDigit);
  const slashIndex = value.indexOf("/");

  if (slashIndex === -1) {
    const digits = digitsOf(value).slice(0, MONTH_LENGTH + yearLength);
    const month = clampMonth(digits.slice(0, MONTH_LENGTH));
    const year = digits.slice(MONTH_LENGTH);
    return digits.length > MONTH_LENGTH ? `${month}/${year}` : month;
  }

  const month = clampMonth(
    digitsOf(value.slice(0, slashIndex)).slice(0, MONTH_LENGTH)
  );
  const year = digitsOf(value.slice(slashIndex + 1)).slice(0, yearLength);
  if (month === "" && year === "") return "";
  return `${month}/${year}`;
};

const completeYear = (year: string, yearDigit: ExpiryYearDigit): string => {
  if (year === "") return year;
  const padded = year.padStart(MONTH_LENGTH, "0");
  if (yearDigit === "two" || padded.length !== 2) return padded;
  const century = Math.floor(new Date().getFullYear() / 100);
  return `${century}${padded}`;
};

/**
 * Fills in what a user leaves implicit when they leave the field: a single
 * digit month or year gets its leading zero, and in MM/YYYY mode a one or two
 * digit year gets the current century (7/27 becomes 07/2027). A three digit
 * year is left as typed for validation to reject. Values already formatted by
 * formatExpiryDate are expected; empty segments stay empty.
 */
export const completeExpiryDate = (
  value: string,
  yearDigit: ExpiryYearDigit
): string => {
  const slashIndex = value.indexOf("/");
  if (slashIndex === -1) {
    return value === "" ? value : value.padStart(MONTH_LENGTH, "0");
  }
  const month = value.slice(0, slashIndex);
  const year = value.slice(slashIndex + 1);
  const completedMonth =
    month === "" ? month : month.padStart(MONTH_LENGTH, "0");
  return `${completedMonth}/${completeYear(year, yearDigit)}`;
};

/**
 * Caret position in the formatted value after an edit at `rawCursor` in
 * `raw`. Counts digits per segment, capped at the segment length, so a digit
 * that formatExpiryDate dropped from a full segment does not push the caret.
 */
export const expiryCaretPosition = (
  raw: string,
  rawCursor: number,
  formatted: string,
  yearDigit: ExpiryYearDigit
): number => {
  const slashIndex = raw.indexOf("/");
  if (slashIndex === -1) {
    return calculateCursorPosition(raw, rawCursor, formatted);
  }

  if (rawCursor <= slashIndex) {
    const digitsBefore = digitsOf(raw.slice(0, rawCursor)).length;
    return Math.min(digitsBefore, MONTH_LENGTH);
  }

  const digitsBefore = digitsOf(raw.slice(slashIndex + 1, rawCursor)).length;
  return (
    formatted.indexOf("/") + 1 + Math.min(digitsBefore, yearLengthOf(yearDigit))
  );
};
